import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Label } from "~/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { AdminApiError, fetchAdminCycles } from "~/lib/admin-api";
import {
  buildAdminCyclesQuery,
  cycleSkipReasonFilters,
  cycleStatusFilters,
  formatCycleSkipReason,
  formatCycleStatus,
  hasNextPage,
} from "~/lib/subscription-cycles";
import {
  dateDisplayProps,
  formatBillingStatus,
  formatDate,
  formatDateTime,
  formatMillingType,
  formatVariety,
  shortId,
} from "~/lib/format";
import type { CycleSkipReason, CycleStatus } from "~/types/admin-cycles";

const pageSize = 50;

function shouldRetry(failureCount: number, error: Error) {
  if (
    error instanceof AdminApiError &&
    error.status >= 400 &&
    error.status < 500
  ) {
    return false;
  }
  return failureCount < 2;
}

const permissionErrorMessage =
  "管理者権限がありません。権限が変更された可能性があります — 再ログインしてください。";

function loadErrorMessage(error: Error): string {
  if (
    error instanceof AdminApiError &&
    (error.status === 401 || error.status === 403)
  ) {
    return permissionErrorMessage;
  }
  return error.message;
}

export function SubscriptionCycles({ accessToken }: { accessToken: string }) {
  const [status, setStatus] = useState<CycleStatus>("skipped");
  const [skipReason, setSkipReason] = useState<CycleSkipReason | "">("");
  const [offset, setOffset] = useState(0);

  const query = buildAdminCyclesQuery({
    status,
    ...(status === "skipped" && skipReason ? { skipReason } : {}),
    limit: pageSize,
    offset,
  });
  const cyclesQuery = useQuery({
    queryKey: ["admin-cycles", query],
    queryFn: ({ signal }) => fetchAdminCycles(accessToken, query, signal),
    retry: shouldRetry,
  });
  const rows = cyclesQuery.data?.cycles ?? [];

  return (
    <section className="space-y-6" aria-label="定期便の回">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="grid gap-2">
            <Label htmlFor="cycle-status">状態で絞り込み</Label>
            <select
              id="cycle-status"
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as CycleStatus);
                setOffset(0);
              }}
            >
              {cycleStatusFilters.map((value) => (
                <option key={value} value={value}>
                  {formatCycleStatus(value)}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cycle-skip-reason">理由で絞り込み</Label>
            <select
              id="cycle-skip-reason"
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50"
              value={skipReason}
              disabled={status !== "skipped"}
              onChange={(event) => {
                setSkipReason(event.target.value as CycleSkipReason | "");
                setOffset(0);
              }}
            >
              <option value="">すべて</option>
              {cycleSkipReasonFilters.map((value) => (
                <option key={value} value={value}>
                  {formatCycleSkipReason(value)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void cyclesQuery.refetch()}
          disabled={cyclesQuery.isFetching}
        >
          {cyclesQuery.isFetching ? "更新中…" : "最新に更新"}
        </Button>
      </div>

      {cyclesQuery.isError ? (
        <Alert variant="destructive">
          <AlertTitle>定期便の回を取得できませんでした</AlertTitle>
          <AlertDescription>
            {loadErrorMessage(cyclesQuery.error)}
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardContent>
          {cyclesQuery.isPending ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              定期便の回を読み込んでいます…
            </p>
          ) : null}
          {cyclesQuery.data ? (
            rows.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                該当する回はありません。
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>対象週</TableHead>
                    <TableHead>契約ID</TableHead>
                    <TableHead>品種 / 精米</TableHead>
                    <TableHead>理由</TableHead>
                    <TableHead>請求</TableHead>
                    <TableHead>配送枠</TableHead>
                    <TableHead>更新</TableHead>
                    <TableHead>検知時刻</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((cycle) => (
                    <TableRow key={cycle.id}>
                      <TableCell
                        {...dateDisplayProps(cycle.target_iso_week, formatDate)}
                      />
                      <TableCell
                        className="font-mono"
                        title={cycle.subscription_id}
                      >
                        {shortId(cycle.subscription_id)}
                      </TableCell>
                      <TableCell>
                        {formatVariety(cycle.variety)} /{" "}
                        {formatMillingType(cycle.milling_type)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={cycle.skip_reason ? "warning" : "outline"}
                          title={cycle.skip_reason ?? undefined}
                        >
                          {formatCycleSkipReason(cycle.skip_reason)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge title={cycle.billing_status} variant="secondary">
                          {formatBillingStatus(cycle.billing_status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span
                          {...dateDisplayProps(
                            cycle.delivery_window_start,
                            formatDate,
                          )}
                        />
                        {" – "}
                        <span
                          {...dateDisplayProps(
                            cycle.delivery_window_end,
                            formatDate,
                          )}
                        />
                      </TableCell>
                      <TableCell
                        {...dateDisplayProps(cycle.updated_at, formatDateTime)}
                      />
                      <TableCell
                        {...dateDisplayProps(
                          cycle.stock_alerted_at,
                          formatDateTime,
                        )}
                      />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : null}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs text-muted-foreground">
          最近更新された回から順に表示しています。スキップの確定時刻は記録していないため、並びと「更新」列は最後の変更時刻です。
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0 || cyclesQuery.isFetching}
            onClick={() => setOffset((current) => Math.max(0, current - pageSize))}
          >
            前の50件
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNextPage(rows.length, pageSize) || cyclesQuery.isFetching}
            onClick={() => setOffset((current) => current + pageSize)}
          >
            次の50件
          </Button>
        </div>
      </div>
    </section>
  );
}
